import React, { useCallback, type ReactNode, type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/standard-button';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/glyph/cross';
// @ts-expect-error - TS2305 - Module '"@atlaskit/onboarding"' has no exported member 'ModalProps'.
import { Modal, type ModalProps } from '@atlaskit/onboarding';

import { media, Box, xcss } from '@atlaskit/primitives';

import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ShortcutScope } from '@atlassian/jira-common-components-keyboard-shortcuts';
import { useIntl } from '@atlassian/jira-intl';
import messages from './messages';

type OnClick = (e: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => void;

export type Props = ModalProps & {
	imageAltText?: string;
	headerContent?: ReactNode;
	withBodyReducedTopPadding?: boolean;
	onClose?: OnClick | undefined;
};

export const BenefitsModal = (props: Props) => {
	const {
		imageAltText,
		image,
		headerContent,
		heading,
		withBodyReducedTopPadding = false,
		actions,
		children,
		onClose,
		...rest
	} = props;
	const { formatMessage } = useIntl();

	const ModalHeader = useCallback(
		() => (
			<Header>
				{image !== undefined && <Image alt={imageAltText} src={image} />}
				{headerContent}
				{onClose && (
					<CloseButtonWrapper>
						<Button
							aria-label={formatMessage(messages.closeModal)}
							spacing="none"
							appearance="subtle"
							iconBefore={
								<CrossIcon
									label={formatMessage(messages.closeModal)}
									primaryColor={token('color.icon.inverse', colors.N0)}
									size="small"
								/>
							}
							onClick={onClose}
						/>
					</CloseButtonWrapper>
				)}
			</Header>
		),
		[formatMessage, headerContent, image, imageAltText, onClose],
	);
	const hasHeading = !!heading;

	return (
		<ShortcutScope>
			<Modal header={ModalHeader} actions={actions} {...rest}>
				<Body withReducedTopPadding={withBodyReducedTopPadding}>
					{hasHeading && (
						<>
							<Heading size="medium" as="h4">
								{heading}
							</Heading>
							<Box xcss={gapBelowHeadingStyles} />
						</>
					)}
					{children}
				</Body>
			</Modal>
		</ShortcutScope>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CloseButtonWrapper = styled.div({
	position: 'absolute',
	top: token('space.200', '16px'),
	right: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Image = styled.img({
	height: 'auto',
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled.div({
	overflow: 'hidden',
	position: 'relative',
	borderRadius: 0,
	[media.above.xs]: {
		borderTopLeftRadius: token('border.radius', '3px'),
		borderTopRightRadius: token('border.radius', '3px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Body = styled.div<{ withReducedTopPadding: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginTop: ({ withReducedTopPadding }) =>
		withReducedTopPadding ? token('space.negative.200', '-16px') : token('space.0', '0px'),
	marginLeft: token('space.negative.250', '-20px'),
	marginRight: token('space.negative.250', '-20px'),
});

const gapBelowHeadingStyles = xcss({
	margin: 'space.0',
	marginBottom: 'space.100',
});
